import React, { useMemo } from 'react';
import { SxProps, Theme, Typography } from '@mui/material';
import { endTrial } from '@utils/common';
import { APP_CONSTANTS } from '@utils/constants';
import { getHost } from '@services/hostUtils';
import { PaymentPlanType } from '@providers/PaymentProvider/PaymentProvider.types';
import { Routes } from '../../../App';

const parsePlanPeriod = (length?: string | null) => {
  if (!length) return null;

  const count = Number(length.match(/\d+/)?.[0]);
  if (!count) return null;

  const unit = /d/i.test(length)
    ? 'day'
    : /m/i.test(length)
    ? 'month'
    : /y/i.test(length)
    ? 'year'
    : null;
  if (!unit) return null;

  return { count, unit };
};

export type PlanDisclosureVariant = 'paywall' | 'consent';

type PlanDisclosureProps = {
  plan?: PaymentPlanType | null;
  variant?: PlanDisclosureVariant;
  sx?: SxProps<Theme>;
};

function PlanDisclosure({
  plan,
  variant = 'paywall',
  sx = [],
}: PlanDisclosureProps) {
  const disclosure = useMemo(() => {
    if (!plan) return null;

    const trialPeriod = parsePlanPeriod(plan.trial_length);
    const trialEndDate = trialPeriod
      ? endTrial(plan.trial_length, trialPeriod.count)
      : undefined;
    if (!trialPeriod || !trialEndDate) return null;

    const renewalPeriod = parsePlanPeriod(plan.length);

    return {
      trialValue: plan.trial_sum,
      trialLabel: `${trialPeriod.count}-${trialPeriod.unit}`,
      trialEndDate,
      renewalPrice: plan.sum,
      renewalPeriodLabel:
        renewalPeriod && renewalPeriod.count > 1
          ? `${renewalPeriod.count} ${renewalPeriod.unit}s`
          : renewalPeriod?.unit ?? 'month',
    };
  }, [plan]);

  if (!disclosure) return null;

  const isConsent = variant === 'consent';

  const supportLink = (
    <a href={`mailto:${APP_CONSTANTS.EMAIL}`} className="link">
      {APP_CONSTANTS.EMAIL}
    </a>
  );

  const cancelLink = (
    <a
      href={Routes.CANCELLATION_POLICY}
      className="link"
      target="_blank"
      rel="noreferrer"
    >
      {getHost(2)}
      {Routes.CANCELLATION_POLICY}
    </a>
  );

  return (
    <Typography
      sx={[
        {
          color: isConsent ? '#7A82A6' : '#908C89',
          fontFamily: 'Avenir400',
          fontSize: '12px',
          lineHeight: 'normal',
          '& .link': {
            color: isConsent ? '#ED744E' : '#908C89',
            textDecoration: 'underline',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {isConsent ? (
        <>
          I understand and agree that I am signing up for a{' '}
          {disclosure.trialLabel} introductory trial and will be charged $
          {disclosure.trialValue} today, and that unless I cancel, my trial will
          automatically convert on {disclosure.trialEndDate} to a paid
          subscription of ${disclosure.renewalPrice} per{' '}
          {disclosure.renewalPeriodLabel}, charged to my card and renewing
          automatically each {disclosure.renewalPeriodLabel} until I cancel. I
          understand and agree that I can cancel at any time before the renewal
          date, with immediate effect, in my account settings, by emailing{' '}
          {supportLink}, or at {cancelLink}. I understand and agree that the $
          {disclosure.trialValue} introductory fee is non-refundable.
        </>
      ) : (
        <>
          Today you will be charged ${disclosure.trialValue} for your{' '}
          {disclosure.trialLabel} introductory trial. Unless you cancel, your
          trial will automatically convert on {disclosure.trialEndDate} to a
          paid subscription of ${disclosure.renewalPrice} per{' '}
          {disclosure.renewalPeriodLabel}, charged to your card and renewing
          automatically each {disclosure.renewalPeriodLabel} until you cancel.
          You can cancel at any time before the renewal date, with immediate
          effect, in your account settings, by emailing {supportLink}, or at{' '}
          {cancelLink}.
        </>
      )}
    </Typography>
  );
}

export default PlanDisclosure;
